// Proton ButtonLike (packages/atoms/src/Button/ButtonLike.tsx), same props
// and defaults: color "weak", size "medium", shape "outline" for weak and
// "solid" otherwise. Classes map 1:1 onto src/proton/components.css.
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

export type ButtonShape = "solid" | "outline" | "ghost" | "underline";
export type ButtonColor = "norm" | "weak" | "danger" | "warning" | "success";
export type ButtonSize = "tiny" | "small" | "medium" | "large";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  shape?: ButtonShape;
  color?: ButtonColor;
  size?: ButtonSize;
  /** Square, icon-only padding. */
  icon?: boolean;
  fullWidth?: boolean;
  pill?: boolean;
  loading?: boolean;
  children?: ReactNode;
}

export function buttonClasses({
  shape, color = "weak", size = "medium", icon, fullWidth, pill,
}: Pick<ButtonProps, "shape" | "color" | "size" | "icon" | "fullWidth" | "pill">): string {
  const s = shape ?? (color === "weak" ? "outline" : "solid");
  const underline = s === "underline";
  return clsx(
    underline ? "button-underline" : "button",
    `button-${s}-${color}`,
    !underline && `button-${size}`,
    !underline && pill && "button-pill",
    !underline && icon && "button-for-icon",
    !underline && fullWidth && "button-full",
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { shape, color, size, icon, fullWidth, pill, loading, className, children, type = "button", disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(buttonClasses({ shape, color, size, icon, fullWidth, pill }), className)}
      {...rest}
    >
      {children}
      {loading && <span className="circle-loader" aria-hidden="true" />}
    </button>
  );
});
